// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/roles/AssignRoleModal.jsx

import React, { useEffect, useMemo, useState } from "react";
import ModalShell from "../../compliance/modals/shared/ModalShell";
import ModalSection from "../../compliance/modals/shared/ModalSection";

function roleLabel(roleKey) {
  return String(roleKey || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function AssignRoleModal({
  open,
  user,
  roles = [],
  onClose,
  onAssign,
  saving,
}) {
  const activeAssignments = useMemo(
    () => new Set(Array.isArray(user?.assigned_roles) ? user.assigned_roles : []),
    [user?.assigned_roles]
  );

  const assignableRoles = useMemo(
    () =>
      roles.filter(
        (role) =>
          role.is_assignable !== false &&
          !activeAssignments.has(role.role_key)
      ),
    [roles, activeAssignments]
  );

  const [roleKey, setRoleKey] = useState("");

  useEffect(() => {
    if (open) {
      setRoleKey(assignableRoles[0]?.role_key || "");
    }
  }, [open, assignableRoles, user?.user_id]);

  if (!user) return null;

  const selectedRole = assignableRoles.find(
    (role) => role.role_key === roleKey
  );

  const submit = async (event) => {
    event.preventDefault();
    if (!roleKey) return;

    await onAssign?.(user.user_id, roleKey);
    onClose?.();
  };

  return (
    <ModalShell
      className="enterprise-admin-modal-shell"
      overlayClassName="enterprise-admin-modal-overlay"
      headerClassName="enterprise-admin-modal-header"
      bodyClassName="enterprise-admin-modal-body"
      open={open}
      eyebrow="Role assignment"
      title="Assign organization role"
      subtitle={user.display_name || user.email || user.user_id}
      onClose={saving ? undefined : onClose}
      maxWidth="680px"
    >
      <form className="enterprise-admin-user-modal-form" onSubmit={submit}>
        <ModalSection
          title="Available role"
          description="The new role applies only to this organization. OASYS enforces its permissions immediately and records the assignment in administrative audit history."
          badge="Permission enforced"
          badgeTone="info"
        >
          {assignableRoles.length ? (
            <>
              <label className="enterprise-admin-user-reason-field">
                <span>Role</span>
                <div className="enterprise-admin-select-wrap">
                  <select
                    value={roleKey}
                    onChange={(event) => setRoleKey(event.target.value)}
                  >
                    {assignableRoles.map((role) => (
                      <option key={role.role_key} value={role.role_key}>
                        {role.role_name || roleLabel(role.role_key)}
                      </option>
                    ))}
                  </select>
                  <span
                    className="enterprise-admin-select-chevron"
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                </div>
              </label>

              {selectedRole ? (
                <div className="enterprise-admin-role-assignment-preview">
                  <strong>
                    {selectedRole.role_name || roleLabel(selectedRole.role_key)}
                  </strong>
                  <p>{selectedRole.description || "Organization authority role."}</p>
                  <span>
                    {selectedRole.permissions?.length || 0} enforced permission
                    {(selectedRole.permissions?.length || 0) === 1 ? "" : "s"}
                  </span>
                </div>
              ) : null}
            </>
          ) : (
            <div className="enterprise-admin-empty compact">
              <strong>No additional roles available</strong>
              <p>
                This member already has every role that can currently be assigned.
              </p>
            </div>
          )}
        </ModalSection>

        <div className="enterprise-admin-user-modal-actions">
          <button
            className="btn btn-primary btn-sm"
            type="submit"
            disabled={saving || !roleKey}
          >
            {saving ? "Assigning role…" : "Assign role"}
          </button>

          <button
            className="btn btn-ghost btn-sm"
            type="button"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
